<template>
    <div class="d-md-flex flex-md-row flex-column">
        <div class="card card-body mb-3 mb-md-0 w-md-50">
            <slot></slot>
        </div>
        <div class="d-flex flex-column align-items-center justify-content-center gap-8 w-md-50">
            <div class="d-flex px-5 py-3 bg-white rounded-pill w-100 w-md-75">
                <div class="d-flex align-items-center w-50 justify-content-center">
                    <i class="fas fa-2x fa-chart-line mr-4"></i>
                    <span class="font-size-h5">{{ __('statistics.impressions') }}</span>
                </div>
                <div class="d-flex align-items-center w-50 justify-content-center">
                    <span class="font-weight-bold font-size-h2 mr-4">
                        {{ getFormattedNumber(statisticsLastMonth.clicks_impressions[0].metrics.impressions) }}
                    </span>
                    <u v-if="statisticsTwoMonthsAgo" class="cursor-pointer" data-toggle="tooltip"
                       data-placement="bottom" data-html="true"
                       :title="toolTipText(getFormattedNumber(statisticsLastMonth.clicks_impressions[0].metrics.impressions),
                                        getFormattedNumber(statisticsTwoMonthsAgo.clicks_impressions[0].metrics.impressions))">
                            <span class="underline" v-if="statisticsTwoMonthsAgo"
                                  :class="[getTextColor(statisticsLastMonth.clicks_impressions[0].metrics.impressions, statisticsTwoMonthsAgo.clicks_impressions[0].metrics.impressions)]">
                                {{
                                    calculateDifference(statisticsLastMonth.clicks_impressions[0].metrics.impressions,
                                        statisticsTwoMonthsAgo.clicks_impressions[0].metrics.impressions)
                                }}%
                            </span>
                    </u>
                </div>
            </div>
            <div class="d-flex px-5 py-3 bg-white rounded-pill w-100 w-md-75">
                <div class="d-flex align-items-center w-50 justify-content-center">
                    <i class="fas fa-2x fa-mouse-pointer mr-4"></i>
                    <span class="font-size-h5">{{ __('statistics.clicks') }}</span>
                </div>
                <div class="d-flex align-items-center w-50 justify-content-center">
                    <span class="font-weight-bold font-size-h2 mr-4">
                        {{ getFormattedNumber(statisticsLastMonth.clicks_impressions[0].metrics.clicks) }}
                    </span>
                    <u v-if="statisticsTwoMonthsAgo" class="cursor-pointer" data-toggle="tooltip"
                       data-placement="bottom" data-html="true"
                       :title="toolTipText(getFormattedNumber(statisticsLastMonth.clicks_impressions[0].metrics.clicks),
                                        getFormattedNumber(statisticsTwoMonthsAgo.clicks_impressions[0].metrics.clicks))">
                            <span
                                :class="[getTextColor(statisticsLastMonth.clicks_impressions[0].metrics.clicks, statisticsTwoMonthsAgo.clicks_impressions[0].metrics.clicks)]">
                                {{
                                    calculateDifference(statisticsLastMonth.clicks_impressions[0].metrics.clicks,
                                        statisticsTwoMonthsAgo.clicks_impressions[0].metrics.clicks)
                                }}%
                            </span>
                    </u>
                </div>
            </div>
            <div class="d-flex px-5 py-3 bg-white rounded-pill w-100 w-md-75">
                <div class="d-flex align-items-center w-50 justify-content-center">
                    <i class="fas fa-2x fa-percentage mr-4"></i>
                    <span class="font-size-h5">{{ __('statistics.ctr') }}</span>
                </div>
                <div class="d-flex align-items-center w-50 justify-content-center">
                    <span class="font-weight-bold font-size-h2 mr-4">
                        {{ roundCtr(statisticsLastMonth.clicks_impressions[0].metrics.ctr) }}%
                    </span>
                    <u v-if="statisticsTwoMonthsAgo" class="cursor-pointer" data-toggle="tooltip"
                       data-placement="bottom" data-html="true"
                       :title="toolTipText(roundCtr(statisticsLastMonth.clicks_impressions[0].metrics.ctr)  + '%',
                                        roundCtr(statisticsTwoMonthsAgo.clicks_impressions[0].metrics.ctr) + '%')">
                            <span v-if="statisticsTwoMonthsAgo"
                                  :class="[getTextColor(statisticsLastMonth.clicks_impressions[0].metrics.ctr, statisticsTwoMonthsAgo.clicks_impressions[0].metrics.ctr)]">
                                {{ roundCtr(statisticsTwoMonthsAgo.clicks_impressions[0].metrics.ctr) }}%
                            </span>
                    </u>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        statisticsLastMonth: {
            types: [Object, Array],
        },
        statisticsTwoMonthsAgo: {
            types: [Object, Array],
        },
        locale: String,
    },
    methods: {
        roundCtr(ctr) {
            return (ctr * 100).toFixed(2)
        },
        calculateDifference(lastMonth, twoMonthsAgo) {
            const result = (lastMonth / twoMonthsAgo) * 100
            if (result < 100) {
                return -(100 - result).toFixed(2)
            } else {
                return '+' + (result - 100).toFixed(2)
            }
        },
        getTextColor(lastMonth, twoMonthsAgo) {
            const result = (lastMonth / twoMonthsAgo) * 100
            return result < 100 ? 'text-danger' : 'text-success'
        },
        toolTipText(lastMonth, twoMonthsAgo) {
            return `
                <div class="container">
                  <div class="row">
                    <div class="col">
                      <p class="font-weight-bold text-xl">${this.__('statistics.monthly_comparison')}</p>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col">
                      <p class="text-left">${this.getMonth(this.statisticsLastMonth.year_month)}</p>
                    </div>
                    <div class="col">
                      <p class="font-weight-bold text-monospace">${lastMonth}</p>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col">
                      <p class="text-left">${this.getMonth(this.statisticsTwoMonthsAgo.year_month)}</p>
                    </div>
                    <div class="col">
                      <p class="font-weight-bold text-monospace">${twoMonthsAgo}</p>
                    </div>
                  </div>
                </div>`
        },
        getMonth(date) {
            const dateObj = new Date(date)
            const month = dateObj.toLocaleString('default', {month: 'long'})
            const lastDay = new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0).getDate()

            return `1. bis ${lastDay}. ${month} ${dateObj.getFullYear()}`
        },
        getFormattedNumber(number) {
            return new Intl.NumberFormat(this.locale).format(number)
        },
    },
    computed: {
        getMonthName() {
            let date = new Date(this.statisticsLastMonth.year_month)

            return date.toLocaleDateString('default', {month: 'long'}) + ' ' + date.getFullYear()
        },
    },
}
</script>